/**
 * FiasAIInput - Chat input for the Fias AI panel.
 *
 * Structural readiness component. Will be used within FiasAIPanel for arche integration.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { Send, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

// In-progress composer drafts, keyed so each Fias AI surface keeps its own text.
// Module-scoped so a draft survives the input unmounting/remounting — leaving the
// chat view for settings/history, minimizing, or closing and reopening the Fias
// AI window all tear down this component. Clears on a full page reload, matching
// the in-window message list (mirrors the chat composer's composerDrafts).
const composerDrafts = new Map<string, string>();

interface FiasAIInputProps {
  onSubmit: (message: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /** When true, focus the textarea on mount so the user can type immediately. */
  autoFocus?: boolean;
  /**
   * When set, the in-progress draft is preserved under this key across the
   * component unmounting/remounting (tab-switch, minimize, close/reopen) — not a
   * full page reload. Omit for ephemeral inputs that shouldn't retain text.
   */
  draftKey?: string;
  /**
   * When set, a Stop button replaces Send while a turn is in flight —
   * cooperative cancel: the server finishes the current step and stops.
   */
  onStop?: () => void;
}

export function FiasAIInput({
  onSubmit,
  disabled = false,
  placeholder = 'Ask Fias AI...',
  autoFocus = false,
  draftKey,
  onStop,
}: FiasAIInputProps) {
  const [value, setValue] = useState(() => (draftKey ? (composerDrafts.get(draftKey) ?? '') : ''));
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Update the value and mirror it into the module-scoped draft store so an
  // in-progress message outlives this component instance (see composerDrafts).
  const updateValue = useCallback(
    (next: string) => {
      setValue(next);
      if (!draftKey) return;
      if (next) composerDrafts.set(draftKey, next);
      else composerDrafts.delete(draftKey);
    },
    [draftKey],
  );

  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  }, []);

  useEffect(() => {
    adjustHeight();
  }, [value, adjustHeight]);

  // Keep the composer focused so a conversation can be carried on by typing
  // alone — no clicking back into the box. This fires:
  //  - on mount (panel opens; the popup unmounts on close, so this re-runs)
  //  - whenever the input transitions back to enabled, i.e. after the assistant
  //    finishes a turn or pauses to ask a question (it's disabled while
  //    "thinking", which blurs the textarea).
  // The caret is placed at the end so a restored draft is ready to continue.
  useEffect(() => {
    if (!autoFocus || disabled) return;
    const el = textareaRef.current;
    if (!el) return;
    el.focus();
    const end = el.value.length;
    el.setSelectionRange(end, end);
  }, [autoFocus, disabled]);

  const handleSubmit = () => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSubmit(trimmed);
    updateValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="border-t border-border p-2">
      <div className="relative">
        <Textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => updateValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className="min-h-[36px] max-h-[120px] pr-10 resize-none rounded-lg text-sm bg-muted/50"
          rows={1}
          style={{ height: 'auto' }}
        />
        {onStop ? (
          <Button
            type="button"
            size="icon"
            variant="destructive"
            onClick={onStop}
            title="Stop"
            aria-label="Stop the assistant"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 rounded-md"
            data-testid="fias-ai-stop"
          >
            <Square className="h-3 w-3 fill-current" />
          </Button>
        ) : (
          <Button
            type="button"
            size="icon"
            disabled={!value.trim() || disabled}
            onClick={handleSubmit}
            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 rounded-md"
          >
            <Send className="h-3 w-3" />
          </Button>
        )}
      </div>
    </div>
  );
}
